"use client";

/**
 * Shared error recovery for the clip `<video>` players (share page, owner
 * detail, meeting/notetaker detail).
 *
 * A transient Chrome decode error (progressive MP4 whose fetch gets
 * suspended/resumed mid-stream, or a seek into an unbuffered range) must not
 * end playback: mint a fresh URL, seek back, resume. Three rules here are
 * load-bearing — removing any of them re-creates the infinite
 * error → re-mint → error loop that was captured live in prod:
 *
 * - A recovery load must never autoplay. The element's `autoplay` attribute
 *   restarts playback on every src swap and races the resume seek on the cold
 *   stream, which re-triggers the decode error. `videoAutoPlay` is therefore
 *   forced off while a recovery is in flight.
 * - Resume is a paused seek issued at `loadeddata`; play happens only after
 *   `seeked`, and only if the viewer was actually watching when the error hit.
 * - The retry budget is restored only after the resumed playback survives
 *   {@link STABLE_PLAYBACK_MS} — never at `loadeddata`. Every loop cycle
 *   contains a `loadeddata`, so resetting there made the cap unreachable.
 */

import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

const MAX_RECOVERIES = 3;
/** Resumed playback must survive this long before retries are restored. */
const STABLE_PLAYBACK_MS = 8000;
/** How often the last provably-decodable playback position is sampled. */
const STABLE_POSITION_SAMPLE_MS = 1000;

export interface ClipPlaybackRecovery {
  /** Autoplay for the element: the caller's wish, forced off mid-recovery. */
  readonly videoAutoPlay: boolean;
  /** Wire to the player's `onError`. */
  readonly handleError: () => void;
  /** Wire to the player's `onLoadedData`. */
  readonly handleLoadedData: () => void;
  /**
   * Take one retry from the shared budget; false when it is exhausted. For
   * error paths outside the media element (e.g. the URL mint action failing).
   */
  readonly spendRetry: () => boolean;
  /** Drop errors and resume state — on clip change or a manual retry. */
  readonly reset: () => void;
}

export function useClipPlaybackRecovery({
  videoRef,
  autoPlay,
  requestNewUrl,
  onExhausted,
}: {
  readonly videoRef: RefObject<HTMLVideoElement | null>;
  /** Whether the initial load should autoplay; recovery loads never do. */
  readonly autoPlay: boolean;
  /** Mint a fresh playback URL (the current one may have expired). */
  readonly requestNewUrl: () => void;
  /** Retry budget used up — the view decides what to show. */
  readonly onExhausted: () => void;
}): ClipPlaybackRecovery {
  const retriesRef = useRef(0);
  const resumeAtRef = useRef<number | null>(null);
  const wasPlayingRef = useRef(false);
  const lastStableRef = useRef<number | null>(null);
  const seekedListenerRef = useRef<(() => void) | null>(null);
  const stableTimerRef = useRef<number | null>(null);
  const [recovering, setRecovering] = useState(false);
  const requestNewUrlRef = useRef(requestNewUrl);
  const onExhaustedRef = useRef(onExhausted);
  requestNewUrlRef.current = requestNewUrl;
  onExhaustedRef.current = onExhausted;

  const clearPending = useCallback((): void => {
    if (stableTimerRef.current !== null) {
      window.clearTimeout(stableTimerRef.current);
      stableTimerRef.current = null;
    }
    const listener = seekedListenerRef.current;
    if (listener) {
      videoRef.current?.removeEventListener("seeked", listener);
      seekedListenerRef.current = null;
    }
  }, [videoRef]);

  useEffect(() => clearPending, [clearPending]);

  // Sample the newest position that provably decodes: playing, not seeking,
  // enough data. A dying seek target never qualifies — recovery falls back
  // here instead of re-entering the position that just killed the pipeline
  // (files with era-split parameter sets die deterministically per position).
  useEffect(() => {
    const id = window.setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused || video.seeking || video.ended) return;
      if (video.readyState < 3 || video.error) return;
      lastStableRef.current = video.currentTime;
    }, STABLE_POSITION_SAMPLE_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [videoRef]);

  const spendRetry = useCallback((): boolean => {
    if (retriesRef.current >= MAX_RECOVERIES) return false;
    retriesRef.current += 1;
    return true;
  }, []);

  const reset = useCallback((): void => {
    clearPending();
    retriesRef.current = 0;
    resumeAtRef.current = null;
    wasPlayingRef.current = false;
    lastStableRef.current = null;
    setRecovering(false);
  }, [clearPending]);

  const handleError = useCallback((): void => {
    // Also cancels a running stability timer: an error inside the stability
    // window must not hand the budget back.
    clearPending();
    const video = videoRef.current;
    if (video && Number.isFinite(video.currentTime) && video.currentTime > 0) {
      // A seek that dies mid-flight reports the deadly target as currentTime
      // (era-split files fail deterministically per position) — bounce back
      // to the last position that provably decoded instead of re-entering it.
      resumeAtRef.current =
        video.seeking && lastStableRef.current !== null
          ? lastStableRef.current
          : video.currentTime;
      // Read before Chrome's post-error pause lands: it decides whether the
      // recovered stream continues by itself or waits for the viewer.
      wasPlayingRef.current = !video.paused && !video.ended;
    }
    if (!spendRetry()) {
      onExhaustedRef.current();
      return;
    }
    // Final attempt: both direct resume targets died — restart from the top,
    // which always decodes (the header parameter set matches the first era).
    if (retriesRef.current >= MAX_RECOVERIES) {
      resumeAtRef.current = null;
    }
    setRecovering(true);
    requestNewUrlRef.current();
  }, [videoRef, clearPending, spendRetry]);

  const handleLoadedData = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    const finish = (playIfPaused: boolean): void => {
      seekedListenerRef.current = null;
      setRecovering(false);
      if (playIfPaused && video.paused) {
        void video.play().catch(() => {});
      }
      if (stableTimerRef.current !== null) {
        window.clearTimeout(stableTimerRef.current);
      }
      stableTimerRef.current = window.setTimeout(() => {
        stableTimerRef.current = null;
        retriesRef.current = 0;
      }, STABLE_PLAYBACK_MS);
    };
    const resumeAt = resumeAtRef.current;
    if (resumeAt === null) {
      // Recovery of a load that never played (error before playback began):
      // nothing to seek to — fall back to the initial autoplay wish. Never
      // while a resume seek is still pending — its `seeked` handler finishes.
      if (recovering && seekedListenerRef.current === null) finish(autoPlay);
      return;
    }
    resumeAtRef.current = null;
    clearPending();
    // A src swap never sets `paused` by itself; if the pipeline errored
    // without pausing, the fresh load would auto-resume straight into the
    // pending seek — the exact race this recovery exists to avoid.
    if (!video.paused) video.pause();
    const onSeeked = (): void => finish(wasPlayingRef.current);
    seekedListenerRef.current = onSeeked;
    video.addEventListener("seeked", onSeeked, { once: true });
    // Paused seek on the fresh stream — autoplay is suppressed while
    // recovering and play() waits for `seeked`, so nothing races it. The
    // browser clamps a target beyond the (possibly still settling) duration.
    video.currentTime = resumeAt;
  }, [videoRef, recovering, autoPlay, clearPending]);

  return {
    videoAutoPlay: autoPlay && !recovering,
    handleError,
    handleLoadedData,
    spendRetry,
    reset,
  };
}
